// EIP-1559 (type 0x02) transaction encoder + signer.
//
// Spec: EIP-2718 envelopes the typed tx with a leading byte (0x02 here),
// followed by the RLP-encoded fields list. EIP-1559 defines the field
// order: [chainId, nonce, maxPriorityFeePerGas, maxFeePerGas, gasLimit,
//         to, value, data, accessList, ...sig?].
//
// Sign over keccak256(0x02 || rlp([... unsigned fields ...])). Append
// (yParity, r, s) to the same list and re-encode for the broadcast
// payload.

import { secp256k1 } from '@noble/curves/secp256k1';
import { keccak_256 } from '@noble/hashes/sha3';
import { hexToBytesExact, hexToUnpaddedBytes, rlpEncode, type RlpInput } from './rlp';

// PreparedTx is the shape the backend returns from /v1/wallets/evm/prepare-tx.
// Every field is 0x-prefixed hex so the iframe can RLP-encode without
// touching decimal big ints. Integer fields are minimal-length already
// (the backend uses `hexUint` / `hexBig` which strip leading zeros).
export type PreparedTx = {
  chain: string;
  chain_id: string;
  nonce: string;
  from: string;
  to: string;
  data: string;
  value: string;
  gas_limit: string;
  max_fee_per_gas: string;
  max_priority_fee_per_gas: string;
};

export type SignedTx = {
  rawTxHex: string; // 0x02… RLP-encoded signed tx, ready for eth_sendRawTransaction
  txHash: string;   // keccak256 of the full signed envelope (the on-chain tx hash)
};

// signEip1559Tx returns a fully signed transaction envelope. The caller
// owns the private key buffer; we don't keep a reference.
export function signEip1559Tx(privateKey: Uint8Array, tx: PreparedTx): SignedTx {
  const chainId = hexToUnpaddedBytes(tx.chain_id);
  const nonce = hexToUnpaddedBytes(tx.nonce);
  const maxPriority = hexToUnpaddedBytes(tx.max_priority_fee_per_gas);
  const maxFee = hexToUnpaddedBytes(tx.max_fee_per_gas);
  const gasLimit = hexToUnpaddedBytes(tx.gas_limit);
  const to = hexToBytesExact(tx.to); // address, leading zeros preserved
  const value = hexToUnpaddedBytes(tx.value);
  const data = hexToBytesExact(tx.data === '0x' ? '0x' : tx.data);
  const accessList: RlpInput = []; // empty by design — see HL recipe in docs

  const unsignedFields: RlpInput = [
    chainId,
    nonce,
    maxPriority,
    maxFee,
    gasLimit,
    to,
    value,
    data,
    accessList,
  ];
  const unsignedPayload = rlpEncode(unsignedFields);
  const unsignedEnvelope = prefixWithType(unsignedPayload);
  const sigHash = keccak_256(unsignedEnvelope);

  const sig = secp256k1.sign(sigHash, privateKey);
  // EIP-1559 uses yParity (0 | 1), not (27 | 28). secp256k1's `sig.recovery`
  // is already 0/1 — exactly what we need.
  const yParity = sig.recovery ?? 0;
  const compact = sig.toCompactRawBytes();
  const r = compact.slice(0, 32);
  const s = compact.slice(32, 64);

  const signedFields: RlpInput = [
    chainId,
    nonce,
    maxPriority,
    maxFee,
    gasLimit,
    to,
    value,
    data,
    accessList,
    yParity === 0 ? new Uint8Array(0) : new Uint8Array([yParity]),
    stripLeadingZeros(r),
    stripLeadingZeros(s),
  ];
  const signedPayload = rlpEncode(signedFields);
  const signedEnvelope = prefixWithType(signedPayload);
  const txHash = keccak_256(signedEnvelope);

  return {
    rawTxHex: '0x' + bytesToHexLower(signedEnvelope),
    txHash: '0x' + bytesToHexLower(txHash),
  };
}

function prefixWithType(payload: Uint8Array): Uint8Array {
  const out = new Uint8Array(1 + payload.length);
  out[0] = 0x02;
  out.set(payload, 1);
  return out;
}

function stripLeadingZeros(b: Uint8Array): Uint8Array {
  let i = 0;
  while (i < b.length - 1 && b[i] === 0) i++;
  return b.subarray(i);
}

function bytesToHexLower(b: Uint8Array): string {
  const hex = '0123456789abcdef';
  let out = '';
  for (let i = 0; i < b.length; i++) {
    out += hex[b[i] >> 4];
    out += hex[b[i] & 0x0f];
  }
  return out;
}
